/**
 * The one single-face panel packer, shared by the designer and the API's
 * Repack, so both lay the same panels from the same inputs.
 */
/**
 * Client-side single-face panel packer for the edit-first solar designer: a
 * port of the server row-packer (`packPanels.ts` + `segmentPackingFaces`)
 * reduced to one face, so per-face rotate / fill edits re-lay that face's
 * array instantly with zero server latency.
 *
 * All public coordinates are lat/lng. A local equirectangular metric frame is
 * built per call around the face centroid (east = x, north = y — the same
 * convention as `panelGeometry.ts`); at building scale (~30 m) the projection
 * error is sub-centimetre.
 */

import { largestFilledRectangle, largestIsland } from './solarArrayShape';
import { type SolarSavingsRegion } from './solarSavingsConstants';
import { roofDistancePerGroundRowM } from './solarMounting';
import {
  roofEdgeAnchorIsValid,
  type SolarRoofEdgeAnchor,
} from './solarRoofEdgeAnchor';
import {
  DEFAULT_SOLAR_PLACEMENT_RULES,
  fixedSolarRowGap,
  insetsFromEdgeSetback,
  packSkipReason,
  solarPanelGaps,
  tiltFrameRowClearanceM,
  type SolarPlacementRules,
} from './solarPlacementRules';

import { isConvexPolygonWithinRing } from './solarConvexFootprint';

export type LatLng = { lat: number; lng: number };

export type FacePolygon = {
  outer: LatLng[];
  holes: LatLng[][];
};

export type PackedFacePanel = {
  corners: [LatLng, LatLng, LatLng, LatLng];
  orientation: 'PORTRAIT' | 'LANDSCAPE';
  faceIndex: number;
  segmentIndex: number;
  annualAcKwh: number;
};

export type Vec2 = { x: number; y: number };
type Ring = Vec2[];

// ── Constants mirrored from the server packer ──
const BOUNDARY_INSET_M = 0.2;
const OBSTRUCTION_PADDING_M = 0.1;
const PANEL_GAP_M = 0.02;
const SCAN_STEP_M = 0.1;
const ROW_PHASE_FRACTIONS = [0, 1 / 4, 1 / 2, 3 / 4];

const M_PER_DEG_LAT = 111_320;
const degToRad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Local metric frame (equirectangular around a reference lat/lng): east = x,
 * north = y, metres. Exported as THE canonical lat/lng ⇄ metres helper for
 * client-side geometry edits (e.g. rotating a face polygon) — raw-degree maths
 * shears shapes because a degree of longitude shrinks with latitude.
 */
export const makeFrame = (ref: LatLng) => {
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(degToRad(ref.lat));
  return {
    toM: (p: LatLng): Vec2 => ({
      x: (p.lng - ref.lng) * mPerDegLng,
      y: (p.lat - ref.lat) * M_PER_DEG_LAT,
    }),
    toLatLng: (v: Vec2): LatLng => ({
      lat: ref.lat + v.y / M_PER_DEG_LAT,
      lng: ref.lng + v.x / mPerDegLng,
    }),
  };
};

const ringCentroid = (pts: LatLng[]): LatLng => {
  let lat = 0;
  let lng = 0;
  for (const p of pts) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / pts.length, lng: lng / pts.length };
};

// ── 2D primitives (metric frame) ──
export const azimuthToUnitVector = (azimuthDegrees: number): Vec2 => ({
  x: Math.sin(degToRad(azimuthDegrees)),
  y: Math.cos(degToRad(azimuthDegrees)),
});

const pointInRing = (point: Vec2, ring: Ring): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const intersects =
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
};

const distPointToSegment = (p: Vec2, a: Vec2, b: Vec2): number => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSq = abx * abx + aby * aby;
  let t =
    lengthSq === 0 ? 0 : ((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * abx), p.y - (a.y + t * aby));
};

const cross = (o: Vec2, a: Vec2, b: Vec2): number =>
  (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

const segmentsIntersect = (a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean => {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  if (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  ) {
    return true;
  }
  const onSegment = (p: Vec2, q: Vec2, r: Vec2): boolean =>
    Math.min(p.x, r.x) <= q.x &&
    q.x <= Math.max(p.x, r.x) &&
    Math.min(p.y, r.y) <= q.y &&
    q.y <= Math.max(p.y, r.y);
  if (d1 === 0 && onSegment(c, a, d)) return true;
  if (d2 === 0 && onSegment(c, b, d)) return true;
  if (d3 === 0 && onSegment(a, c, b)) return true;
  if (d4 === 0 && onSegment(a, d, b)) return true;
  return false;
};

const ringIntersectsSegment = (ring: Ring, a: Vec2, b: Vec2): boolean => {
  for (let i = 0; i < ring.length; i++) {
    if (segmentsIntersect(ring[i], ring[(i + 1) % ring.length], a, b)) {
      return true;
    }
  }
  return false;
};

// ── Plane frame: ground plan ⇆ roof-plane coordinates ──
export const createPlaneFrame = (
  reference: Vec2,
  pitchDegrees: number,
  orientationAzimuthDegrees: number
) => {
  const downhill = azimuthToUnitVector(orientationAzimuthDegrees);
  const crossSlope = { x: downhill.y, y: -downhill.x };
  const cosPitch = Math.cos(degToRad(pitchDegrees));
  return {
    toPlane: (p: Vec2): Vec2 => {
      const dx = p.x - reference.x;
      const dy = p.y - reference.y;
      return {
        x: dx * crossSlope.x + dy * crossSlope.y,
        y: (dx * -downhill.x + dy * -downhill.y) / cosPitch,
      };
    },
    toGround: (p: Vec2): Vec2 => ({
      x: reference.x + p.x * crossSlope.x + p.y * cosPitch * -downhill.x,
      y: reference.y + p.x * crossSlope.y + p.y * cosPitch * -downhill.y,
    }),
  };
};

/** Rotate array axes within the physical roof plane, not the roof's slope. */
export const createRoofPackingFrame = (
  reference: Vec2,
  pitchDegrees: number,
  roofAzimuthDegrees: number,
  rowAzimuthDegrees: number
) => {
  const roof = createPlaneFrame(reference, pitchDegrees, roofAzimuthDegrees);
  const uphill = azimuthToUnitVector(rowAzimuthDegrees + 180);
  const direction = roof.toPlane({
    x: reference.x + uphill.x,
    y: reference.y + uphill.y,
  });
  const length = Math.hypot(direction.x, direction.y);
  const v = { x: direction.x / length, y: direction.y / length };
  const u = { x: v.y, y: -v.x };
  return {
    toPlane: (p: Vec2): Vec2 => {
      const q = roof.toPlane(p);
      return { x: q.x * u.x + q.y * u.y, y: q.x * v.x + q.y * v.y };
    },
    toGround: (p: Vec2): Vec2 =>
      roof.toGround({ x: p.x * u.x + p.y * v.x, y: p.x * u.y + p.y * v.y }),
  };
};

type PlaneRect = { u0: number; v0: number; u1: number; v1: number };

/** Bearing whose array cross-axis is parallel to the chosen physical roof edge. */
export const roofEdgeAnchorAzimuth = (
  face: FacePolygon,
  anchor: SolarRoofEdgeAnchor,
  pitchDegrees: number,
  roofAzimuthDegrees: number,
  onFrames = false
): number | null => {
  if (!roofEdgeAnchorIsValid(anchor, face.outer)) return null;
  const frame = makeFrame(anchor.endpoints[0]);
  const roof = createPlaneFrame(
    { x: 0, y: 0 },
    onFrames ? 0 : pitchDegrees,
    roofAzimuthDegrees
  );
  const ring = face.outer.map((point) => roof.toPlane(frame.toM(point)));
  const a = ring[anchor.edgeIndex];
  const b = ring[(anchor.edgeIndex + 1) % ring.length];
  const area = ring.reduce((sum, p, i) => {
    const q = ring[(i + 1) % ring.length];
    return sum + p.x * q.y - q.x * p.y;
  }, 0);
  const sign = area >= 0 ? 1 : -1;
  const inward = { x: -(b.y - a.y) * sign, y: (b.x - a.x) * sign };
  const ground = roof.toGround(inward);
  // Packing v is uphill, the opposite of the public compass row bearing.
  return ((Math.atan2(-ground.x, -ground.y) * 180) / Math.PI + 360) % 360;
};

const rectCorners = (rect: PlaneRect): [Vec2, Vec2, Vec2, Vec2] => [
  { x: rect.u0, y: rect.v0 },
  { x: rect.u1, y: rect.v0 },
  { x: rect.u1, y: rect.v1 },
  { x: rect.u0, y: rect.v1 },
];

/** Minkowski sum with the packer's square obstruction clearance. */
const paddedFootprint = (corners: Vec2[], by: number): Vec2[] => {
  const points = corners
    .flatMap((p) =>
      [-by, by].flatMap((x) =>
        [-by, by].map((y) => ({ x: p.x + x, y: p.y + y }))
      )
    )
    .sort((a, b) => a.x - b.x || a.y - b.y);
  const half = (points: Vec2[]) => {
    const hull: Vec2[] = [];
    for (const p of points) {
      while (
        hull.length >= 2 &&
        cross(hull[hull.length - 2], hull[hull.length - 1], p) <= 0
      )
        hull.pop();
      hull.push(p);
    }
    return hull.slice(0, -1);
  };
  return [...half(points), ...half([...points].reverse())];
};

export type PackFaceArgs = {
  face: FacePolygon;
  pitchDegrees: number;
  /**
   * The compass azimuth panel ROWS orient to. The face's own downslope azimuth
   * for the default/fill layout; offset by the rotation control to re-angle the
   * array.
   */
  rowAzimuthDegrees: number;
  obstructions: LatLng[][];
  panelWidthMeters: number;
  panelHeightMeters: number;
  faceIndex: number;
  segmentIndex: number;
  /** Indicative per-panel annual energy proxy (kWh) stamped on each panel. */
  energyPerPanelKwh: number;
  /** Compass downslope of the face, for the north-facing skip. */
  azimuthDegrees?: number;
  /** Generate-time sun access; unknown faces are not skipped. */
  sunAccessPercent?: number | null;
  rules?: SolarPlacementRules;
  /** Explicitly resolved mounting; pitchDegrees is always the physical roof. */
  mounting?: { onFrames: boolean; pitchDegrees: number };
  /** Constrain every packing candidate to this panel orientation. */
  orientation?: PackedFacePanel['orientation'];
  roofEdgeAnchor?: SolarRoofEdgeAnchor | null;
  /**
   * When false, still packs a north / shallow / poor-sun roof. Explicit packing uses
   * true (the default).
   */
  respectRoofSkips?: boolean;
  /** Drives the eave/hip inset derivation; defaults to UK. */
  region?: SolarSavingsRegion;
};

export type FacePanelFitArgs = Pick<
  PackFaceArgs,
  | 'face'
  | 'pitchDegrees'
  | 'rowAzimuthDegrees'
  | 'obstructions'
  | 'azimuthDegrees'
  | 'rules'
  | 'mounting'
  | 'region'
  | 'roofEdgeAnchor'
>;

const segmentDistance = (a: Vec2, b: Vec2, c: Vec2, d: Vec2): number =>
  segmentsIntersect(a, b, c, d)
    ? 0
    : Math.min(
        distPointToSegment(a, c, d),
        distPointToSegment(b, c, d),
        distPointToSegment(c, a, b),
        distPointToSegment(d, a, b)
      );

/** One usable-area predicate for packing and committing array edits. */
const prepareFacePanelFit = ({
  face,
  pitchDegrees,
  rowAzimuthDegrees,
  obstructions,
  azimuthDegrees,
  rules = DEFAULT_SOLAR_PLACEMENT_RULES,
  mounting,
  roofEdgeAnchor,
  region = 'uk',
}: FacePanelFitArgs) => {
  const onFrames =
    mounting?.onFrames ?? pitchDegrees <= rules.flatRoof.maxSlopeDeg;
  const insets = roofEdgeAnchor
    ? {
        boundaryInsetM: rules.edgeSetbackM,
        eaveInsetM: rules.edgeSetbackM,
        hipInsetM: rules.edgeSetbackM,
      }
    : insetsFromEdgeSetback(rules.edgeSetbackM, region);
  const { boundaryInsetM, eaveInsetM, hipInsetM } = insets;
  const { columnGapM, rowGapM: normalRowGapM } = solarPanelGaps(rules);

  const frame = makeFrame(ringCentroid(face.outer));
  const outerM = face.outer.map(frame.toM);
  const holesM = face.holes.map((hole) => hole.map(frame.toM));
  const obstructionsM = obstructions.map((ring) => ring.map(frame.toM));

  const reference = outerM[0];
  const roofAzimuth = azimuthDegrees ?? rowAzimuthDegrees;
  const roofPlane = createPlaneFrame(reference, pitchDegrees, roofAzimuth);
  // Tilt-frame modules have their own absolute panel plane. Pack their horizontal
  // footprints; flush modules lie in the physical roof, regardless of row angle.
  const plane = onFrames
    ? createPlaneFrame(reference, 0, rowAzimuthDegrees)
    : createRoofPackingFrame(
        reference,
        pitchDegrees,
        roofAzimuth,
        rowAzimuthDegrees
      );

  const outer = outerM.map(plane.toPlane);
  const holes = holesM.map((hole) => hole.map(plane.toPlane));
  const zones = obstructionsM.map((ring) => ring.map(plane.toPlane));

  let minU = Infinity;
  let minV = Infinity;
  let maxU = -Infinity;
  let maxV = -Infinity;
  for (const p of outer) {
    if (p.x < minU) minU = p.x;
    if (p.y < minV) minV = p.y;
    if (p.x > maxU) maxU = p.x;
    if (p.y > maxV) maxV = p.y;
  }

  const EAVE_BAND_M = 0.3;
  const MIN_EAVE_EDGE_M = 1;
  const MIN_EAVE_EDGE_FRACTION = 0.25;
  const isDiagonal = (a: Vec2, b: Vec2): boolean => {
    const du = Math.abs(b.x - a.x);
    const dv = Math.abs(b.y - a.y);
    return du > 0.3 && dv > 0.3 && Math.min(du, dv) / Math.max(du, dv) > 0.25;
  };
  const isLevel = (a: Vec2, b: Vec2): boolean =>
    Math.abs(b.x - a.x) > 0.3 &&
    !isDiagonal(a, b) &&
    Math.abs(b.y - a.y) < Math.abs(b.x - a.x);
  const roofOutline = outerM.map(roofPlane.toPlane);
  const roofMinV = Math.min(...roofOutline.map((p) => p.y));
  let eaveFloor = roofMinV;
  {
    const minEaveEdge = Math.max(
      MIN_EAVE_EDGE_M,
      MIN_EAVE_EDGE_FRACTION *
        (Math.max(...roofOutline.map((p) => p.x)) -
          Math.min(...roofOutline.map((p) => p.x)))
    );
    let lowestLevel = Infinity;
    for (let i = 0; i < roofOutline.length; i++) {
      const a = roofOutline[i];
      const b = roofOutline[(i + 1) % roofOutline.length];
      if (!isLevel(a, b)) continue;
      if (Math.hypot(b.x - a.x, b.y - a.y) < minEaveEdge) continue;
      lowestLevel = Math.min(lowestLevel, Math.min(a.y, b.y));
    }
    if (lowestLevel !== Infinity) eaveFloor = lowestLevel;
  }
  type EdgeClass = 'eave' | 'hip' | 'square';
  const edgeClass = (a: Vec2, b: Vec2): EdgeClass => {
    if (
      a.y <= eaveFloor + EAVE_BAND_M &&
      b.y <= eaveFloor + EAVE_BAND_M &&
      (eaveFloor === roofMinV || isLevel(a, b))
    ) {
      return 'eave';
    }
    return isDiagonal(a, b) ? 'hip' : 'square';
  };
  const fits = (corners: Vec2[]): boolean => {
    if (!isConvexPolygonWithinRing(corners, outer)) return false;
    // Measure the entire physical footprint against every edge, including
    // concave insets that can pass between four otherwise valid corners.
    const metric = (q: Vec2) =>
      onFrames ? roofPlane.toPlane(plane.toGround(q)) : q;
    for (let i = 0; i < outer.length; i++) {
      const a = outer[i];
      const b = outer[(i + 1) % outer.length];
      const cls = edgeClass(
        roofOutline[i],
        roofOutline[(i + 1) % roofOutline.length]
      );
      const inset = {
        eave: eaveInsetM,
        hip: hipInsetM,
        square: boundaryInsetM,
      }[cls];
      for (let j = 0; j < corners.length; j++) {
        if (
          segmentDistance(
            metric(corners[j]),
            metric(corners[(j + 1) % corners.length]),
            metric(a),
            metric(b)
          ) <
          inset - 1e-9
        )
          return false;
      }
    }
    for (const hole of holes) {
      if (
        corners.some((p, i) =>
          ringIntersectsSegment(hole, p, corners[(i + 1) % corners.length])
        )
      )
        return false;
      if (hole.length > 0 && pointInRing(hole[0], corners)) return false;
      if (pointInRing(corners[0], hole)) return false;
    }
    const padded = zones.length
      ? paddedFootprint(corners, OBSTRUCTION_PADDING_M)
      : [];
    for (const zone of zones) {
      if (
        zone.some((p) => pointInRing(p, padded)) ||
        padded.some(
          (p, i) =>
            pointInRing(p, zone) ||
            ringIntersectsSegment(zone, p, padded[(i + 1) % padded.length])
        )
      ) {
        return false;
      }
    }
    return true;
  };

  return {
    onFrames,
    frame,
    plane,
    minU,
    minV,
    maxU,
    maxV,
    roofAzimuth,
    columnGapM,
    normalRowGapM,
    outer,
    outerM,
    roofPlane,
    boundaryInsetM,
    fits,
  };
};

export const createFacePanelFitPredicate = (args: FacePanelFitArgs) => {
  if (args.face.outer.length < 3) return (_corners: readonly LatLng[]) => false;
  const { frame, plane, fits } = prepareFacePanelFit(args);
  return (corners: readonly LatLng[]) =>
    fits(corners.map((p) => plane.toPlane(frame.toM(p))));
};

/**
 * Densely pack one roof face with panel rectangles, square to `rowAzimuth`,
 * clipped to (face − boundary inset − padded obstructions). Mirrors the
 * server's multi-strategy row packer. Island and complete-rectangle rules are
 * scored on the cropped layout so landscape is not chosen just because an L
 * held more panels before the crop. Returns lat/lng panels.
 */
export const packFace = (args: PackFaceArgs): PackedFacePanel[] => {
  const {
    face,
    pitchDegrees,
    rowAzimuthDegrees,
    panelWidthMeters,
    panelHeightMeters,
    faceIndex,
    segmentIndex,
    energyPerPanelKwh,
    azimuthDegrees,
    sunAccessPercent,
    respectRoofSkips = true,
    orientation: forcedOrientation,
  } = args;
  const rules: SolarPlacementRules =
    args.rules ?? DEFAULT_SOLAR_PLACEMENT_RULES;

  if (face.outer.length < 3) return [];
  if (
    args.roofEdgeAnchor &&
    (!(panelWidthMeters > 0) ||
      !(panelHeightMeters > 0) ||
      !Number.isFinite(panelWidthMeters + panelHeightMeters) ||
      !roofEdgeAnchorIsValid(args.roofEdgeAnchor, face.outer))
  )
    return [];
  if (
    respectRoofSkips &&
    packSkipReason({
      pitchDegrees,
      azimuthDegrees,
      sunAccessPercent,
      rules,
      onFrames: args.mounting?.onFrames,
    })
  ) {
    return [];
  }
  const {
    onFrames,
    frame,
    plane,
    minU,
    minV,
    maxU,
    maxV,
    roofAzimuth,
    columnGapM,
    normalRowGapM,
    outer,
    outerM,
    roofPlane,
    boundaryInsetM,
    fits: fitsCorners,
  } = prepareFacePanelFit(args);
  const fits = (rect: PlaneRect) => fitsCorners(rectCorners(rect));
  const tiltRad = onFrames
    ? ((args.mounting?.pitchDegrees ?? rules.flatRoof.frameTiltDeg) * Math.PI) /
      180
    : 0;
  const rowProjection = roofDistancePerGroundRowM(
    { pitchDegrees, azimuthDegrees: roofAzimuth },
    rowAzimuthDegrees
  );
  const fixedRows = onFrames && rules.flatRoof.rowSpacingMode === 'fixed';
  const siteLatitude =
    face.outer.reduce((sum, p) => sum + p.lat, 0) / face.outer.length;
  const rowGapM = fixedRows
    ? fixedSolarRowGap(rules.flatRoof, normalRowGapM, onFrames) / rowProjection
    : normalRowGapM;
  const physicalDims = (orientation: 'PORTRAIT' | 'LANDSCAPE') => {
    const w = orientation === 'PORTRAIT' ? panelWidthMeters : panelHeightMeters;
    const h = orientation === 'PORTRAIT' ? panelHeightMeters : panelWidthMeters;
    return { w, h: onFrames ? h * Math.cos(tiltRad) : h };
  };
  const dims = (orientation: 'PORTRAIT' | 'LANDSCAPE') => {
    const flush =
      orientation === 'PORTRAIT'
        ? { w: panelWidthMeters, h: panelHeightMeters }
        : { w: panelHeightMeters, h: panelWidthMeters };
    if (!onFrames) return flush;
    if (fixedRows) return physicalDims(orientation);
    // Rows pack on the ground plane: footprint plus the shade-free clear
    // distance behind the raised edge (shared with the server packer).
    return {
      w: flush.w,
      h:
        physicalDims(orientation).h +
        tiltFrameRowClearanceM({
          slopeLengthM: flush.h,
          frameTiltDegrees: (tiltRad * 180) / Math.PI,
          roofPitchDegrees: pitchDegrees,
          roofAzimuthDegrees: roofAzimuth,
          arrayAzimuthDegrees: rowAzimuthDegrees,
          latitudeDegrees: siteLatitude,
          rowSpacingMultiplier: rules.flatRoof.rowSpacingMultiplier,
        }),
    };
  };

  type Placed = { rect: PlaneRect; orientation: 'PORTRAIT' | 'LANDSCAPE' };

  // An anchored array has one fixed lattice, not a bounding-box phase search.
  // All candidates use the final physical footprint and final inter-row pitch.
  const anchor = args.roofEdgeAnchor;
  let anchorDistance: ((p: Vec2) => number) | undefined;
  const packAnchored = (orientation: 'PORTRAIT' | 'LANDSCAPE'): Placed[] => {
    if (!anchor) return [];
    const a = outer[anchor.edgeIndex];
    const b = outer[(anchor.edgeIndex + 1) % outer.length];
    const ar = roofPlane.toPlane(plane.toGround(a));
    const br = roofPlane.toPlane(plane.toGround(b));
    const ring = outerM.map(roofPlane.toPlane);
    const area = ring.reduce((sum, p, i) => {
      const q = ring[(i + 1) % ring.length];
      return sum + p.x * q.y - q.x * p.y;
    }, 0);
    const length = Math.hypot(br.x - ar.x, br.y - ar.y);
    if (length < 1e-9) return [];
    const sign = area >= 0 ? 1 : -1;
    const normal = {
      x: (-(br.y - ar.y) / length) * sign,
      y: ((br.x - ar.x) / length) * sign,
    };
    const distance = (p: Vec2) => {
      const q = roofPlane.toPlane(plane.toGround(p));
      return (q.x - ar.x) * normal.x + (q.y - ar.y) * normal.y;
    };
    anchorDistance = distance;
    const { w, h } = dims(orientation);
    const physicalH = physicalDims(orientation).h;
    const tangent = { x: normal.y, y: -normal.x };
    const startRoof =
      (br.x - ar.x) * tangent.x + (br.y - ar.y) * tangent.y >= 0 ? ar : br;
    const origin = plane.toPlane(
      roofPlane.toGround({
        x: startRoof.x + normal.x * anchor.offsetM,
        y: startRoof.y + normal.y * anchor.offsetM,
      })
    );
    const support = Math.min(
      ...rectCorners({
        u0: origin.x,
        v0: origin.y,
        u1: origin.x + w,
        v1: origin.y + physicalH,
      }).map(distance)
    );
    const delta = anchor.offsetM - support;
    let shifted = plane.toPlane(
      roofPlane.toGround({
        x: startRoof.x + normal.x * (anchor.offsetM + delta),
        y: startRoof.y + normal.y * (anchor.offsetM + delta),
      })
    );
    // Fix the along-edge phase from the endpoint and minimum setback too.
    // Obstacles and concavities can remove cells, never move the lattice.
    const alongSupport = Math.min(
      ...rectCorners({
        u0: shifted.x,
        v0: shifted.y,
        u1: shifted.x + w,
        v1: shifted.y + physicalH,
      }).map((point) => {
        const q = roofPlane.toPlane(plane.toGround(point));
        return (
          (q.x - startRoof.x) * tangent.x + (q.y - startRoof.y) * tangent.y
        );
      })
    );
    const alongShift = boundaryInsetM - alongSupport + 1e-8;
    const shiftedRoof = roofPlane.toPlane(plane.toGround(shifted));
    shifted = plane.toPlane(
      roofPlane.toGround({
        x: shiftedRoof.x + tangent.x * alongShift,
        y: shiftedRoof.y + tangent.y * alongShift,
      })
    );
    const colPitch = w + columnGapM;
    const rowPitch = h + rowGapM;
    const placed: Placed[] = [];
    for (
      let r = Math.floor((minV - shifted.y) / rowPitch);
      r <= Math.ceil((maxV - shifted.y) / rowPitch);
      r++
    ) {
      for (
        let c = Math.floor((minU - shifted.x) / colPitch);
        c <= Math.ceil((maxU - shifted.x) / colPitch);
        c++
      ) {
        const u0 = shifted.x + c * colPitch;
        const v0 = shifted.y + r * rowPitch;
        const rect = { u0, v0, u1: u0 + w, v1: v0 + h };
        const physical = { ...rect, v1: v0 + physicalH };
        if (
          rectCorners(physical).some(
            (point) => distance(point) < anchor.offsetM - 1e-8
          )
        )
          continue;
        if (fits(physical)) placed.push({ rect, orientation });
      }
    }
    return placed;
  };

  const packRow = (
    v0: number,
    orientation: 'PORTRAIT' | 'LANDSCAPE'
  ): PlaneRect[] => {
    const { w, h } = dims(orientation);
    if (v0 + h > maxV) return [];
    const row: PlaneRect[] = [];
    let u = minU;
    while (u + w <= maxU) {
      const rect: PlaneRect = { u0: u, v0, u1: u + w, v1: v0 + h };
      if (fits(rect)) {
        row.push(rect);
        u += w + columnGapM;
      } else {
        u += SCAN_STEP_M;
      }
    }
    return row;
  };

  type Strategy = 'PORTRAIT' | 'LANDSCAPE' | 'MIXED';
  const packWithStrategy = (strategy: Strategy, vPhaseM: number): Placed[] => {
    const placed: Placed[] = [];
    let v = minV + vPhaseM;
    while (v < maxV) {
      const rowOptions: Array<'PORTRAIT' | 'LANDSCAPE'> =
        strategy === 'MIXED' ? ['PORTRAIT', 'LANDSCAPE'] : [strategy];
      let bestRow: PlaneRect[] = [];
      let bestOrientation: 'PORTRAIT' | 'LANDSCAPE' = rowOptions[0];
      for (const orientation of rowOptions) {
        const row = packRow(v, orientation);
        if (
          row.length > bestRow.length ||
          (row.length === bestRow.length &&
            row.length > 0 &&
            dims(orientation).h < dims(bestOrientation).h)
        ) {
          bestRow = row;
          bestOrientation = orientation;
        }
      }
      if (bestRow.length > 0) {
        for (const rect of bestRow)
          placed.push({ rect, orientation: bestOrientation });
        v += dims(bestOrientation).h + rowGapM;
      } else {
        v += SCAN_STEP_M;
      }
    }
    return placed;
  };

  const rectsOverlap = (a: PlaneRect, b: PlaneRect): boolean =>
    a.u0 < b.u1 + columnGapM &&
    a.u1 > b.u0 - columnGapM &&
    a.v0 < b.v1 + rowGapM &&
    a.v1 > b.v0 - rowGapM;

  const packGreedy = (
    orientations: readonly ['PORTRAIT' | 'LANDSCAPE', 'PORTRAIT' | 'LANDSCAPE'],
    reverseU: boolean,
    reverseV: boolean
  ): Placed[] => {
    const placed: Placed[] = [];
    for (const orientation of orientations) {
      const { w, h } = dims(orientation);
      for (
        let v = reverseV ? maxV - h : minV;
        reverseV ? v >= minV : v + h <= maxV;
        v += reverseV ? -SCAN_STEP_M : SCAN_STEP_M
      ) {
        for (
          let u = reverseU ? maxU - w : minU;
          reverseU ? u >= minU : u + w <= maxU;
          u += reverseU ? -SCAN_STEP_M : SCAN_STEP_M
        ) {
          const rect: PlaneRect = { u0: u, v0: v, u1: u + w, v1: v + h };
          if (placed.some((p) => rectsOverlap(rect, p.rect))) continue;
          if (!fits(rect)) continue;
          placed.push({ rect, orientation });
          const skip = w + columnGapM - SCAN_STEP_M;
          u += reverseU ? -skip : skip;
        }
      }
    }
    return placed;
  };

  const candidates: Placed[][] = [];
  const oneOrientation =
    !!forcedOrientation || rules.singleOrientation || rules.quadrilateralArrays;
  const strategies: Strategy[] = ['PORTRAIT', 'LANDSCAPE'];
  if (forcedOrientation)
    strategies.splice(0, strategies.length, forcedOrientation);
  else if (!oneOrientation) strategies.push('MIXED');
  for (const strategy of strategies) {
    if (anchor) {
      if (strategy !== 'MIXED') candidates.push(packAnchored(strategy));
      continue;
    }
    const rowPitch =
      dims(strategy === 'LANDSCAPE' ? 'LANDSCAPE' : 'PORTRAIT').h + rowGapM;
    for (const fraction of ROW_PHASE_FRACTIONS) {
      candidates.push(packWithStrategy(strategy, fraction * rowPitch));
    }
  }
  if (!oneOrientation && !anchor) {
    for (const orientations of [
      ['PORTRAIT', 'LANDSCAPE'],
      ['LANDSCAPE', 'PORTRAIT'],
    ] as const) {
      for (const reverseU of [false, true]) {
        for (const reverseV of [false, true]) {
          candidates.push(packGreedy(orientations, reverseU, reverseV));
        }
      }
    }
  }

  const gapFill = (
    placed: Placed[],
    orientations: ReadonlyArray<'PORTRAIT' | 'LANDSCAPE'>
  ): Placed[] => {
    const filled = [...placed];
    for (const orientation of orientations) {
      const { w, h } = dims(orientation);
      for (let v = minV; v + h <= maxV; v += SCAN_STEP_M) {
        for (let u = minU; u + w <= maxU; u += SCAN_STEP_M) {
          const rect: PlaneRect = { u0: u, v0: v, u1: u + w, v1: v + h };
          if (filled.some((p) => rectsOverlap(rect, p.rect))) continue;
          if (!fits(rect)) continue;
          filled.push({ rect, orientation });
          u += w + columnGapM - SCAN_STEP_M;
        }
      }
    }
    return filled;
  };

  const packRigidGrid = (
    orientation: 'PORTRAIT' | 'LANDSCAPE',
    nCols: number,
    nRows: number,
    u0: number,
    v0: number
  ): Placed[] | null => {
    const { w, h } = dims(orientation);
    const placed: Placed[] = [];
    for (let r = 0; r < nRows; r++) {
      for (let c = 0; c < nCols; c++) {
        const rect: PlaneRect = {
          u0: u0 + c * (w + columnGapM),
          v0: v0 + r * (h + rowGapM),
          u1: u0 + c * (w + columnGapM) + w,
          v1: v0 + r * (h + rowGapM) + h,
        };
        if (
          !fits(
            anchor
              ? { ...rect, v1: rect.v0 + physicalDims(orientation).h }
              : rect
          )
        )
          return null;
        placed.push({ rect, orientation });
      }
    }
    return placed;
  };

  /**
   * Railable N×M from an eave-up row packing. Keeps that packing's
   * orientation and row azimuth so Apply cannot turn the array off the eave
   * by searching a landscape block in the bounding box.
   */
  const inscribeGridFromRows = (placed: Placed[]): Placed[] => {
    if (placed.length === 0) return [];
    const orientation = placed[0].orientation;
    if (placed.some((p) => p.orientation !== orientation)) return [];
    const { w } = dims(orientation);
    const colPitch = w + columnGapM;
    const round = (x: number) => Math.round(x / 0.05) * 0.05;
    const rowMap = new Map<number, Placed[]>();
    for (const panel of placed) {
      const key = round(panel.rect.v0);
      const row = rowMap.get(key);
      if (row) row.push(panel);
      else rowMap.set(key, [panel]);
    }
    const rows = [...rowMap.entries()]
      .toSorted((a, b) => a[0] - b[0])
      .map(([, row]) => row.toSorted((a, b) => a.rect.u0 - b.rect.u0));
    let bestGrid: Placed[] = [];
    for (let i = 0; i < rows.length; i++) {
      for (let j = i; j < rows.length; j++) {
        const slice = rows.slice(i, j + 1);
        const left = Math.max(...slice.map((row) => row[0].rect.u0));
        const right = Math.min(
          ...slice.map((row) => row[row.length - 1].rect.u1)
        );
        const nCols = Math.floor((right - left + columnGapM) / colPitch);
        const nRows = slice.length;
        if (nCols < 1 || nRows * nCols <= bestGrid.length) continue;
        const blockW = nCols * w + (nCols - 1) * columnGapM;
        const u0 = anchor
          ? left
          : left + Math.max(0, right - left - blockW) / 2;
        const v0 = slice[0][0].rect.v0;
        const grid = packRigidGrid(orientation, nCols, nRows, u0, v0);
        if (grid && grid.length > bestGrid.length) bestGrid = grid;
      }
    }
    return bestGrid;
  };

  const cropToRules = (placed: Placed[]): Placed[] => {
    let next = placed;
    if (rules.singleIsland && next.length > 1) {
      next = largestIsland(next, rules.panelGapM, { columnGapM, rowGapM });
    }
    if (rules.quadrilateralArrays && next.length > 1) {
      next = largestFilledRectangle(next);
    }
    return next;
  };

  const cropFirst = rules.singleIsland || rules.quadrilateralArrays;
  const leadCount = candidates.reduce((m, c) => Math.max(m, c.length), 0);
  let best: Placed[] = [];
  let bestPortrait = 0;
  const consider = (shaped: Placed[]) => {
    if (anchor && anchorDistance && shaped.length > 0) {
      const nearest = Math.min(
        ...shaped.flatMap(({ rect, orientation }) =>
          rectCorners({
            ...rect,
            v1: rect.v0 + physicalDims(orientation).h,
          }).map(anchorDistance!)
        )
      );
      if (Math.abs(nearest - anchor.offsetM) > 1e-7) return;
    }
    const portrait = shaped.filter((p) => p.orientation === 'PORTRAIT').length;
    if (
      shaped.length > best.length ||
      (shaped.length === best.length && portrait > bestPortrait)
    ) {
      best = shaped;
      bestPortrait = portrait;
    }
  };

  if (rules.quadrilateralArrays) {
    const ofOrientation = (orientation: 'PORTRAIT' | 'LANDSCAPE') =>
      candidates.filter(
        (candidate) =>
          candidate.length > 0 &&
          candidate.every((p) => p.orientation === orientation)
      );
    for (const candidate of ofOrientation('PORTRAIT')) {
      consider(inscribeGridFromRows(candidate));
    }
    // Landscape only if portrait could not form a block: a bbox landscape
    // search can turn arrays off the eave.
    if (best.length === 0) {
      for (const candidate of ofOrientation('LANDSCAPE')) {
        consider(inscribeGridFromRows(candidate));
      }
    }
    if (best.length === 0) {
      for (const candidate of candidates) {
        consider(cropToRules(candidate));
      }
    }
  } else {
    // Match the server packer: no gap-fill when one orientation is required,
    // so Apply cannot rotate a portrait array by stuffing landscape panels in.
    const fillOrientations =
      oneOrientation || anchor ? null : (['PORTRAIT', 'LANDSCAPE'] as const);
    for (const candidate of candidates) {
      if (!cropFirst && candidate.length < leadCount - 1) continue;
      const filled = fillOrientations
        ? gapFill(candidate, fillOrientations)
        : candidate;
      consider(cropFirst ? cropToRules(filled) : filled);
    }
  }

  const packed = best.map(({ rect, orientation }) => {
    // Packing rectangles reserve inter-row clearance; a visible module does not.
    const physical = { ...rect, v1: rect.v0 + physicalDims(orientation).h };
    const corners = rectCorners(physical).map((c) =>
      frame.toLatLng(plane.toGround(c))
    ) as [LatLng, LatLng, LatLng, LatLng];
    return {
      corners,
      orientation,
      faceIndex,
      segmentIndex,
      annualAcKwh: energyPerPanelKwh,
    };
  });
  if (rules.minPanelsPerRoof > 0 && packed.length < rules.minPanelsPerRoof) {
    return [];
  }
  return packed;
};

/**
 * Build a roof-plan rectangle from two opposite corners `a` and `b`, with its
 * sides parallel/perpendicular to `azimuthDegrees` (a compass azimuth). Used by
 * the "New roof area" tool: the installer clicks two points and gets a clean
 * rectangle oriented to the roof slope rather than to north, which `packFace`
 * then fills. Returned as a hole-less lat/lng polygon (outer ring, 4 vertices).
 */
export const buildOrientedRectangle = (
  a: LatLng,
  b: LatLng,
  azimuthDegrees: number
): FacePolygon => {
  const frame = makeFrame(a);
  const am = frame.toM(a);
  const bm = frame.toM(b);
  const downhill = azimuthToUnitVector(azimuthDegrees);
  const crossSlope = { x: downhill.y, y: -downhill.x };
  const project = (p: Vec2) => ({
    u: p.x * crossSlope.x + p.y * crossSlope.y,
    v: p.x * downhill.x + p.y * downhill.y,
  });
  const pa = project(am);
  const pb = project(bm);
  const u0 = Math.min(pa.u, pb.u);
  const u1 = Math.max(pa.u, pb.u);
  const v0 = Math.min(pa.v, pb.v);
  const v1 = Math.max(pa.v, pb.v);
  const toGround = (u: number, v: number): Vec2 => ({
    x: u * crossSlope.x + v * downhill.x,
    y: u * crossSlope.y + v * downhill.y,
  });
  const outer = (
    [
      [u0, v0],
      [u1, v0],
      [u1, v1],
      [u0, v1],
    ] as const
  ).map(([u, v]) => frame.toLatLng(toGround(u, v)));
  return { outer, holes: [] };
};

/**
 * Axis-aligned lat/lng bounding box of a set of face polygons (outer rings —
 * holes can't extend a face), expanded by `paddingFraction` of its span on
 * every side. Feeds `map.fitBounds` for the Roof step's auto-fit: the padding
 * keeps the roof off the viewport edges so edge vertices stay grabbable.
 * Returns null when there are no vertices to fit. A minimum pad (~1 m) guards
 * a degenerate zero-span box from pinning the map at max zoom.
 */
export const facesBoundingBox = (
  faces: FacePolygon[],
  paddingFraction: number
): { sw: LatLng; ne: LatLng } | null => {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  for (const face of faces) {
    for (const point of face.outer) {
      if (point.lat < minLat) minLat = point.lat;
      if (point.lat > maxLat) maxLat = point.lat;
      if (point.lng < minLng) minLng = point.lng;
      if (point.lng > maxLng) maxLng = point.lng;
    }
  }
  if (!Number.isFinite(minLat)) return null;
  const minPadDegrees = 1 / M_PER_DEG_LAT; // ≈ 1 m
  const latPad = Math.max((maxLat - minLat) * paddingFraction, minPadDegrees);
  const lngPad = Math.max((maxLng - minLng) * paddingFraction, minPadDegrees);
  return {
    sw: { lat: minLat - latPad, lng: minLng - lngPad },
    ne: { lat: maxLat + latPad, lng: maxLng + lngPad },
  };
};

/**
 * Plan-view area (m²) of a lat/lng polygon, holes subtracted. Shoelace over a
 * local equirectangular metric frame — sub-centimetre accurate at building
 * scale. Used to label redrawn / added faces in the designer.
 */
export const polygonAreaMeters2 = (polygon: FacePolygon): number => {
  if (polygon.outer.length < 3) return 0;
  const frame = makeFrame(ringCentroid(polygon.outer));
  const ringArea = (ring: LatLng[]): number => {
    const pts = ring.map(frame.toM);
    let sum = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      sum += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
    }
    return Math.abs(sum) / 2;
  };
  const outerArea = ringArea(polygon.outer);
  const holesArea = polygon.holes.reduce(
    (sum, hole) => (hole.length >= 3 ? sum + ringArea(hole) : sum),
    0
  );
  return Math.max(0, outerArea - holesArea);
};

/** Shortest distance (m) from a point to a segment, in the metric frame. */
const pointSegmentDistance = (p: Vec2, a: Vec2, b: Vec2): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared)
  );
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};

/** Shortest distance (m) between two closed rings that do not overlap. */
const ringsMinDistance = (a: Ring, b: Ring): number => {
  let best = Infinity;
  for (const point of a) {
    for (let i = 0; i < b.length; i++) {
      const distance = pointSegmentDistance(point, b[i], b[(i + 1) % b.length]);
      if (distance < best) best = distance;
    }
  }
  for (const point of b) {
    for (let i = 0; i < a.length; i++) {
      const distance = pointSegmentDistance(point, a[i], a[(i + 1) % a.length]);
      if (distance < best) best = distance;
    }
  }
  return best;
};

/**
 * The margin at which an obstruction OUTSIDE a face can still cost it a
 * panel: the packer insets panels from the boundary and pads every zone, so
 * a zone within (inset + padding) of the outline still clips the nearest
 * row.
 */
export const OBSTRUCTION_REACH_METERS =
  BOUNDARY_INSET_M + OBSTRUCTION_PADDING_M;

/**
 * Could this obstruction ring change how `polygon` packs? True when the ring
 * overlaps the face outline or comes within `marginMeters` of it. Lets the
 * designer re-pack only the faces an added/removed zone actually touches and
 * leave every other face's layout (and the installer's own panel counts)
 * alone. Holes are ignored: a zone inside a hole packs nothing either way, so
 * the answer is conservative rather than wrong.
 */
export const ringTouchesPolygon = (
  ring: LatLng[],
  polygon: FacePolygon,
  marginMeters: number = OBSTRUCTION_REACH_METERS
): boolean => {
  if (ring.length < 3 || polygon.outer.length < 3) return false;
  const frame = makeFrame(ringCentroid(polygon.outer));
  const zone = ring.map(frame.toM);
  const outer = polygon.outer.map(frame.toM);

  const bounds = (points: Ring) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return { minX, minY, maxX, maxY };
  };
  const zb = bounds(zone);
  const fb = bounds(outer);
  if (
    zb.minX - marginMeters > fb.maxX ||
    zb.maxX + marginMeters < fb.minX ||
    zb.minY - marginMeters > fb.maxY ||
    zb.maxY + marginMeters < fb.minY
  ) {
    return false;
  }

  for (const point of zone) if (pointInRing(point, outer)) return true;
  for (const point of outer) if (pointInRing(point, zone)) return true;
  for (let i = 0; i < zone.length; i++) {
    if (ringIntersectsSegment(outer, zone[i], zone[(i + 1) % zone.length])) {
      return true;
    }
  }
  return ringsMinDistance(zone, outer) <= marginMeters;
};

/**
 * Cap a freshly packed face at the number of panels the installer chose,
 * keeping the best-yielding ones — the same rule the per-face count slider
 * applies. Panels already excluded (off-property, say) stay excluded and
 * don't use up the allowance. Returns the input untouched when the packing
 * came back at or below the cap, so a face whose count the new layout can no
 * longer reach simply keeps everything it has.
 */
export const capIncludedPanels = <
  T extends { annualAcKwh: number; included: boolean },
>(
  panels: T[],
  maxIncluded: number
): T[] => {
  const eligible = panels
    .map((panel, index) => ({ panel, index }))
    .filter(({ panel }) => panel.included);
  if (eligible.length <= maxIncluded) return panels;
  const keep = new Set(
    eligible
      .sort(
        (a, b) => b.panel.annualAcKwh - a.panel.annualAcKwh || a.index - b.index
      )
      .slice(0, Math.max(0, maxIncluded))
      .map(({ index }) => index)
  );
  return panels.map((panel, index) =>
    panel.included && !keep.has(index) ? { ...panel, included: false } : panel
  );
};
